import * as React from 'react';
import LegacyNotificationChannel from '../models/LegacyNotificationChannel';
import LegacyNotificationManagementService from '../../shared/services/LegacyNotificationManagementService';
import { identity } from '../../core/auth/identityAuth';
import Loading from '../../shared/components/loading/Loading';
import BlockedIcon from '@material-ui/icons/NotInterested';
import LanguageIcon from '@material-ui/icons/Language';
import LockIcon from '@material-ui/icons/Lock';
import FtpIcon from '@material-ui/icons/FolderOpen';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  WithStyles,
  Paper,
} from '@material-ui/core';
import { withStyles, StyleRules } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import Email from '@material-ui/icons/Email';
import { withRouter, RouteComponentProps } from 'react-router';
import LegacyNotificationChannelTypeId from '../models/LegacyNotificationChannelTypeId';

const styles: StyleRules = {
  container: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    width: 'calc(100% + 24px)',
    margin: '-12px',
  },
  item1: {
    flex: '0 0 0',
    padding: '12px',
    boxSizing: 'border-box',
  },
  item2: {
    flex: '1 1 0',
    padding: '12px',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  assetViewDisclaimer: {
    padding: 16,
    marginBottom: 15,
  },
};

interface IState {
  notifications: LegacyNotificationChannel[];
  isLoading: boolean;
}

interface IProps extends WithStyles<typeof styles>, RouteComponentProps<any> {
  assetView?: boolean;
}

class LegacyNotificationOverview extends React.Component<IProps> {
  public static defaultProps = {
    assetView: false,
  };

  public state: IState = {
    notifications: [],
    isLoading: true,
  };

  public async componentDidMount() {
    await this.getNotificationChannels();
  }

  public getNotificationChannels = async () => {
    if (identity['nxt:entity:id']) {
      const data = await LegacyNotificationManagementService.GetNotificationChannels();
      this.setState({ ...this.state, notifications: data, isLoading: false });
    }
  };

  public getNotificationChannelName(typeId: number) {
    switch (typeId) {
      case LegacyNotificationChannelTypeId.Email:
        return 'Email';
      case LegacyNotificationChannelTypeId.Webhook:
        return 'Webhook';
      case LegacyNotificationChannelTypeId.AS2:
        return 'AS2';
      case LegacyNotificationChannelTypeId.FTP:
        return 'FTP';
      case LegacyNotificationChannelTypeId.SFTP:
        return 'SFTP';
      default:
        return '';
    }
  }

  public getNotificationChannelIcon(typeId: number) {
    switch (typeId) {
      case LegacyNotificationChannelTypeId.Email:
        return <Email />;
      case LegacyNotificationChannelTypeId.Webhook:
        return <LanguageIcon />;
      case LegacyNotificationChannelTypeId.AS2:
        return <LockIcon />;
      case LegacyNotificationChannelTypeId.FTP:
        return <FtpIcon />;
      case LegacyNotificationChannelTypeId.SFTP:
        return <FtpIcon />;
      default:
        return '';
    }
  }

  public getNotificationChannelEditDisabled(typeId: number) {
    return typeId === LegacyNotificationChannelTypeId.AS2;
  }

  public renderAssetViewDisclaimer() {
    const { assetView, classes } = this.props;

    if (!assetView) {
      return null;
    }

    return (
      <Paper className={classes.assetViewDisclaimer}>
        <Typography>
          It is currently not possible to customize the notification channel per subscription. This
          feature is in development and will be made available in the near future.
        </Typography>
      </Paper>
    );
  }

  public render() {
    return (
      <div>
        {this.state.isLoading && (
          <div>
            <Loading />
          </div>
        )}
        {this.renderAssetViewDisclaimer()}
        {
          <Grid container={true} spacing={24} className="padding-bottom-24">
            {this.state.notifications.map(this.renderNotification)}
          </Grid>
        }
      </div>
    );
  }

  private renderNotification = (notification: LegacyNotificationChannel) => {
    return (
      <Grid item={true} key={notification.id}>
        <Card>
          <CardContent>
            <Grid
              container={true}
              wrap="nowrap"
              spacing={16}
              className="padding-bottom-6 margin-left-8"
            >
              <Grid item={true}>
                {this.getNotificationChannelIcon(notification.notificationChannelTypeId)}
              </Grid>
              <Grid item={true}>
                <Tooltip
                  title={this.getNotificationChannelName(notification.notificationChannelTypeId)}
                  placement="bottom-start"
                >
                  <Typography variant="subheading" noWrap={true}>
                    {this.getNotificationChannelName(notification.notificationChannelTypeId)}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>
            <List>
              <ListItem>
                {notification.isDefault && <CheckIcon />}
                {!notification.isDefault && <BlockedIcon />}
                <ListItemText secondary={'DEFAULT'} />
              </ListItem>
              <li>
                <Divider />
              </li>
              <ListItem>
                {notification.isEnabled && <CheckIcon />}
                {!notification.isEnabled && <BlockedIcon />}
                <ListItemText secondary={'ACTIVE'} />
              </ListItem>
              <li>
                <Divider />
              </li>
              <Tooltip title={notification.channel.address}>
                <ListItem>
                  <ListItemText
                    secondaryTypographyProps={{ noWrap: true }}
                    secondary={notification.channel.address}
                  />
                </ListItem>
              </Tooltip>
            </List>
            <Button
              disabled={
                this.props.assetView ||
                this.getNotificationChannelEditDisabled(notification.notificationChannelTypeId)
              }
              onClick={() =>
                this.props.history.push('/organization/notifications/' + notification.id)
              }
            >
              Edit
            </Button>
          </CardContent>
        </Card>
      </Grid>
    );
  }
}

export default withRouter(withStyles(styles)(LegacyNotificationOverview));



// WEBPACK FOOTER //
// ./src/modules/organization/components/LegacyNotificationOverview.tsx